import React from 'react';
import classes from './Home12.module.scss';
import { Link } from 'react-router-dom';
import Home12Header from './Home12Header';

const Home12 = () => {
    const heroRef = React.useRef(null);

    // Parallax & Scroll Reveal
    React.useEffect(() => {
        window.scrollTo(0, 0);

        // Parallax scroll handler
        const handleScroll = () => {
            const yOffset = window.scrollY;
            if (heroRef.current) {
                heroRef.current.style.backgroundPositionY = `${yOffset * 0.5}px`;
            }

            const parallaxEls = document.querySelectorAll('.js-parallax');
            parallaxEls.forEach(el => {
                const speed = parseFloat(el.getAttribute('data-speed')) || 0.15;
                el.style.transform = `translateY(${yOffset * speed}px)`;
            });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });

        // Intersection Observer for Animations
        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add(classes.visible);
                    obs.unobserve(entry.target);
                }
            });
        }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

        const animEls = document.querySelectorAll(`.${classes.animate}`);
        animEls.forEach(el => observer.observe(el));

        return () => {
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, []);

    // 提供できるシステムのキーワード（企業ロゴの代わりに表示）
    const serviceKeywords = ['EFFICIENCY', 'WORKFLOW', 'AX', 'PAPERLESS', 'AUTOMATION', 'PAYMENTS', 'CRM', 'MARKETING'];
    // 無限スクロール用に2倍にする
    const marqueeItems = [...serviceKeywords, ...serviceKeywords];
    const featurePanels = [
        {
            image: '/react-assets/home10/home10-feature-nocode.jpg',
            alt: '3台のスマートフォンにアプリ画面が表示されたプロダクト写真',
            title: '作業効率化',
            sectionId: 'home12-feature-efficiency',
            description: '使いやすさを徹底的に追求。難しいマニュアルがなくても、現場の誰もが直感的に操作できる洗練されたデザインを提供します。',
            details: [
                {
                    left: { icon: '/react-assets/home10/icons/home10-icon-drag.png', text: '迷わず使える操作感' },
                    right: { icon: '/react-assets/home10/icons/home10-icon-template.png', text: 'シンプルな画面構成' }
                },
                {
                    left: { icon: '/react-assets/home10/icons/home10-icon-flow.png', text: '現場目線の設計' },
                    right: { icon: '/react-assets/home10/icons/home10-icon-preview.png', text: '高いデザイン性' }
                }
            ]
        },
        {
            image: '/react-assets/home10/home10-feature-update.jpg',
            alt: 'スマートフォンとノートPCにアプリ開発画面が表示されたプロダクト写真',
            title: '現場促進AX',
            sectionId: 'home12-feature-ax',
            description: '納品してそのまま放置はしません。変化し続けるビジネス環境に合わせて機能アップデートを継続し、システムの陳腐化を防ぎます。',
            details: [
                {
                    left: { icon: '/react-assets/home10/icons/home10-icon-update.png', text: '継続的な機能追加' },
                    right: { icon: '/react-assets/home10/icons/home10-icon-test.png', text: '最新環境への適応' }
                },
                {
                    left: { icon: '/react-assets/home10/icons/home10-icon-history.png', text: '定期的な改善対応' },
                    right: { icon: '/react-assets/home10/icons/home10-icon-team.png', text: '運用負荷を最小化' }
                }
            ]
        },
        {
            image: '/react-assets/home10/home10-feature-analytics.jpg',
            alt: '複数のスマートフォンに分析UIが表示され斜めに並んだ写真',
            title: '業務管理',
            sectionId: 'home12-feature-management',
            description: '利用データに基づき、業務改善や売上向上のための最適なアクションを御社に直接ご提案します。意思決定をデータドリブンに切り替えます。',
            details: [
                {
                    left: { icon: '/react-assets/home10/icons/home10-icon-flow.png', text: 'リアルタイムな可視化' },
                    right: { icon: '/react-assets/home10/icons/home10-icon-preview.png', text: '課題要素の特定' }
                },
                {
                    left: { icon: '/react-assets/home10/icons/home10-icon-update.png', text: 'KPIに基づいた分析' },
                    right: { icon: '/react-assets/home10/icons/home10-icon-test.png', text: 'データに基づく提案' }
                }
            ]
        },
        {
            image: '/react-assets/home10/home10-feature-support.jpg',
            alt: 'タブレットとスマートフォンに開発ワークフロー画面が表示された写真',
            title: '万全のサポート体制',
            description: 'システム導入時から運用開始後まで、しっかりと寄り添う安心のサポート体制。御社のIT推進をトータルでバックアップします。',
            details: [
                {
                    left: { icon: '/react-assets/home10/icons/home10-icon-template.png', text: '専任担当者の伴走支援' },
                    right: { icon: '/react-assets/home10/icons/home10-icon-flow.png', text: 'トラブル時の迅速対応' }
                },
                {
                    left: { icon: '/react-assets/home10/icons/home10-icon-update.png', text: '利用定着までの教育' },
                    right: { icon: '/react-assets/home10/icons/home10-icon-team.png', text: '長期的なパートナーシップ' }
                }
            ]
        }
    ];

    const tabLinks = [
        { label: '効率化', targetId: 'home12-feature-efficiency' },
        { label: 'AX', targetId: 'home12-feature-ax' },
        { label: '管理', targetId: 'home12-feature-management' },
        { label: '自動化', targetId: 'home12-panel-automation' },
        { label: '集客', targetId: 'home12-panel-marketing' }
    ];

    const linkPanels = [
        {
            targetId: 'home12-panel-automation',
            tag: 'AUTOMATION',
            title: '自動化・決済',
            description: 'ペーパーレス化、帳簿自動化、オンライン決済までを分断せずに連携。日々の事務負荷を下げ、処理スピードを高めます。',
            image: '/react-assets/home10/home10-card-meeting.jpg'
        },
        {
            targetId: 'home12-panel-marketing',
            tag: 'MARKETING',
            title: '集客システム',
            description: '顧客管理とSNS導線をつなぎ、見込み顧客からリピートまでを一気通貫で設計。集客から運用まで継続改善します。',
            image: '/react-assets/home10/home10-card-dashboard-side.jpg'
        }
    ];

    const smoothScrollTo = (targetId) => {
        const target = document.getElementById(targetId);
        if (!target) {
            return;
        }

        const header = document.querySelector('[data-home12-header]');
        const headerOffset = header ? header.getBoundingClientRect().height + 8 : 80;
        const top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top, behavior: 'smooth' });
    };

    return (
        <div className={classes.root}>
            <Home12Header mode="sticky" />

            {/* Hero Section */}
            <section
                ref={heroRef}
                className={classes.hero}
                style={{
                    backgroundImage: "linear-gradient(180deg, rgba(9, 20, 36, 0.14) 0%, rgba(8, 21, 39, 0.42) 62%, rgba(8, 21, 39, 0.58) 100%), url('/react-assets/home10/home10-hero-bg-portrait.jpg')",
                    backgroundPosition: "center 0px"
                }}
            >
                <div className={classes.heroContent}>
                    <div className={`${classes.subBrand} ${classes.animate} ${classes.fadeInUp}`}>collabo <span>nexus</span></div>
                    <h1 className={`${classes.catchCopy} ${classes.animate} ${classes.fadeInUp} ${classes.delay1}`}>
                        日本の現場で使える、<br />
                        Web / Appの実行基盤をひとつに。
                    </h1>

                    <div className={`js-parallax ${classes.heroObject} ${classes.animate} ${classes.zoomIn} ${classes.delay2}`} data-speed="0.05">
                        <img src="/react-assets/home10/home10-hero-object.png" alt="日本のオフィスで業務ツールを使う若い女性と男性の透過オブジェクト" />
                    </div>

                    <p className={`${classes.subCopy} ${classes.animate} ${classes.fadeInUp} ${classes.delay3}`}>
                        ノーコードとAIで、開発から運用改善までを高速化。
                    </p>

                    <Link to="/services" className={`${classes.ctaButton} ${classes.animate} ${classes.fadeInUp} ${classes.delay4}`}>詳しくはこちら</Link>
                </div>
            </section>

            {/* Tabs Section */}
            <div className={classes.tabsWrapper}>
                <div className={classes.tabsScroll}>
                    {tabLinks.map((tab, index) => (
                        <div
                            key={tab.targetId}
                            className={`${classes.tab} ${index === 0 ? classes.active : ''}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => smoothScrollTo(tab.targetId)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    smoothScrollTo(tab.targetId);
                                }
                            }}
                        >
                            {tab.label}
                        </div>
                    ))}
                </div>
            </div>

            {/* Services Keyword Slider */}
            <div className={classes.clients}>
                <div className={classes.marquee}>
                    {marqueeItems.map((logo, index) => (
                        <div key={index} className={classes.logoItem}>{logo}</div>
                    ))}
                </div>
            </div>

            {/* Resource Cards Section */}
            <section className={classes.resources}>
                <div className={classes.cardsScroll}>
                    <div className={`${classes.card} ${classes.animate} ${classes.fadeInUp}`}>
                        <div className={`${classes.cardImage} ${classes.hoverZoom}`}>
                            <img src="/react-assets/home10/home10-card-dashboard-main.jpg" alt="日本のオフィスのWebサービスダッシュボード画面" />
                        </div>
                        <div className={classes.cardBody}>
                            <h3 className={classes.cardTitle}>日本の現場で使える<br />作業効率化と業務管理システム</h3>
                        </div>
                    </div>
                    <div className={`${classes.card} ${classes.animate} ${classes.fadeInUp} ${classes.delay1}`}>
                        <div className={`${classes.cardImage} ${classes.hoverZoom}`}>
                            <img src="/react-assets/home10/home10-card-meeting.jpg" alt="日本のオフィスで社内アプリを相談する若い女性と男性" />
                        </div>
                        <div className={classes.cardBody}>
                            <h3 className={classes.cardTitle}>ペーパーレス・帳簿自動化<br />オンライン決済システム</h3>
                        </div>
                    </div>
                    <div className={`${classes.card} ${classes.animate} ${classes.fadeInUp} ${classes.delay2}`}>
                        <div className={`${classes.cardImage} ${classes.hoverZoom}`}>
                            <img src="/react-assets/home10/home10-card-dashboard-side.jpg" alt="日本のオフィスで使われるSaaSダッシュボード" />
                        </div>
                        <div className={classes.cardBody}>
                            <h3 className={classes.cardTitle}>顧客管理システムと<br />SNS導線を考慮した集客システム</h3>
                        </div>
                    </div>
                </div>
            </section>

            {/* Big Metrics Typography */}
            <section className={classes.metrics}>
                <h2 className={`${classes.statement} ${classes.animate} ${classes.zoomIn}`}>
                    <span className={classes.largeText}>現場</span>の力を<span className={classes.largeText}>最大化</span>する<br />
                    <span className={classes.highlight}>AI × DX</span> が生み出す<br />
                    次世代AXプラットフォーム
                </h2>
            </section>

            {/* Features (特長) */}
            <section className={classes.featuresBlock}>
                <div className={`${classes.sectionHeader} ${classes.animate} ${classes.fadeInUp}`}>
                    <h2 className={classes.sectionTitle}>collabo 4つの特長</h2>
                    <p className={classes.sectionSub}>ビジネスを躍進させる、圧倒的な利便性と拡張性</p>
                </div>
                <div className={classes.featuresGrid}>
                    {featurePanels.map((feature, index) => {
                        return (
                            <div
                                id={feature.sectionId || undefined}
                                className={`${classes.featureCard} ${classes.animate} ${classes.fadeInUp}`}
                                key={feature.title}
                            >
                                <div className={`${classes.featureIcon} ${classes.hoverZoom}`}>
                                    <img src={feature.image} alt={feature.alt} />
                                </div>
                                <h3>{feature.title}</h3>
                                <p>{feature.description}</p>
                                <div className={classes.featureMeta}>
                                    {feature.details.map((pair, index) => (
                                        <div className={classes.featureMetaPair} key={`${feature.title}-${index}`}>
                                            <div className={classes.featureMetaIcons}>
                                                <span className={classes.metaIcon}>
                                                    <img className={classes.metaIconImage} src={pair.left.icon} alt="" />
                                                </span>
                                                <span className={classes.metaIcon}>
                                                    <img className={classes.metaIconImage} src={pair.right.icon} alt="" />
                                                </span>
                                            </div>
                                            <div className={classes.featureMetaTexts}>
                                                <span>{pair.left.text}</span>
                                                <span>{pair.right.text}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </section>

            <section className={classes.linkPanelsBlock}>
                <div className={`${classes.sectionHeader} ${classes.animate} ${classes.fadeInUp}`}>
                    <h2 className={classes.sectionTitle}>追加ソリューション</h2>
                    <p className={classes.sectionSub}>自動化と集客を、運用まで見据えて実装します</p>
                </div>
                <div className={classes.linkPanelsWrap}>
                    {linkPanels.map((panel, index) => (
                        <div
                            id={panel.targetId}
                            key={panel.targetId}
                            className={`${classes.serviceRow} ${index % 2 === 1 ? classes.rowReverse : ''} ${classes.animate} ${classes.fadeInUp}`}
                        >
                            <div className={classes.serviceImgBox}>
                                <div className={classes.imgWrapper}>
                                    <img src={panel.image} alt={panel.title} />
                                </div>
                            </div>
                            <div className={classes.serviceTextBox}>
                                <div className={classes.svcTag}>{panel.tag}</div>
                                <h3 className={classes.svcTitle}>{panel.title}</h3>
                                <p className={classes.svcDesc}>{panel.description}</p>
                                <Link to="/services" className={classes.svcBtn}>サービス詳細へ &rarr;</Link>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* News / イベント */}
            <section className={classes.newsBlock}>
                <div className={`${classes.sectionHeader} ${classes.animate} ${classes.fadeInUp}`}>
                    <h2 className={classes.sectionTitle}>お知らせ</h2>
                </div>
                <ul className={classes.newsList}>
                    <li className={`${classes.animate} ${classes.fadeInUp}`}>
                        <span className={classes.date}>2024.04.15</span>
                        <span className={classes.category}>リリース</span>
                        <span className={classes.newsText}>日本の現場で使える作業効率化アプリと、汎用的な業務管理システムの提供を開始しました</span>
                    </li>
                    <li className={`${classes.animate} ${classes.fadeInUp} ${classes.delay1}`}>
                        <span className={classes.date}>2024.04.05</span>
                        <span className={classes.category}>リリース</span>
                        <span className={classes.newsText}>DX・AIを用いて現場を促進させるAX機能と、難しくない直感的なUI/UXデザインを実装しました</span>
                    </li>
                    <li className={`${classes.animate} ${classes.fadeInUp} ${classes.delay2}`}>
                        <span className={classes.date}>2024.03.20</span>
                        <span className={classes.category}>機能追加</span>
                        <span className={classes.newsText}>ペーパーレス、帳簿の自動化、およびオンライン決済システムへの標準対応を完了しました</span>
                    </li>
                    <li className={`${classes.animate} ${classes.fadeInUp} ${classes.delay2}`}>
                        <span className={classes.date}>2024.03.10</span>
                        <span className={classes.category}>リリース</span>
                        <span className={classes.newsText}>顧客管理システムおよび、SNSからの導線を考慮した集客システムの提供を開始しました</span>
                    </li>
                    <li className={`${classes.animate} ${classes.fadeInUp} ${classes.delay3}`}>
                        <span className={classes.date}>2024.03.01</span>
                        <span className={classes.category}>サポート</span>
                        <span className={classes.newsText}>データに基づく分析提案と万全のサポート体制を確立。納品後も進化し続ける機能アップデートを提供します</span>
                    </li>
                </ul>
            </section>

            {/* Bottom CTA */}
            <section className={classes.bottomCta}>
                <div className={`${classes.ctaContainer} ${classes.animate} ${classes.zoomIn}`}>
                    <h2>アプリ開発の常識を変える。まずは資料でご確認ください。</h2>
                    <div className={classes.ctaButtons}>
                        <Link to="/download" className={classes.btnPrimary}>無料で資料請求する</Link>
                        <Link to="/contact" className={classes.btnSecondary}>オンライン相談を予約する</Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className={classes.footer}>
                <div className={classes.slogan}>Practical DX for Every Team</div>

                <div className={classes.footerLinks}>
                    <Link to="/company">運営会社</Link>
                    <Link to="/privacy">プライバシーポリシー</Link>
                    <Link to="/contact">お問い合わせ</Link>
                </div>

                <div className={classes.companyInfo}>
                    <div className={classes.name}>コラボ株式会社</div>
                    <p>
                        〒921-8163<br />
                        石川県金沢市横川７丁目２６<br />
                        TEL: 076-287-0026
                    </p>
                    <p style={{ marginTop: '10px' }}>&copy; {new Date().getFullYear()} collabo Inc.</p>
                </div>
            </footer>
        </div>
    );
};

export default Home12;
